import {
  mergeTemplateDesign,
  templateDesignToCssVars,
} from "@webdo/site-templates/builder-core";
import type {
  SiteBuilderDesign,
} from "@webdo/site-templates/builder";

export type BuilderBackgroundPickerLabels = {
  custom: string;
  customColor: string;
  default: string;
  gradient: string;
  previewButton: string;
  previewText: string;
  previewTitle: string;
  theme: string;
  gradientOptions: Record<BuilderBackgroundGradientOptionId, string>;
  themeOptions: Record<BuilderBackgroundThemeOptionId, string>;
};

export type BuilderBackgroundThemeOptionId =
  | "accent"
  | "background"
  | "default"
  | "inverse"
  | "primary"
  | "surface";

export type BuilderBackgroundGradientOptionId =
  | "brand"
  | "inverse"
  | "soft"
  | "surface";

export type BuilderBackgroundPickerValue = {
  backgroundColor: string;
  backgroundImage: string;
  textColor: string;
};

export type BuilderBackgroundThemeOption = {
  accentColor: string;
  backgroundColor: string;
  backgroundValue: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  id: BuilderBackgroundThemeOptionId;
  isDefault?: boolean;
  textColor: string;
  textValue: string;
};

export type BuilderBackgroundGradientOption = {
  buttonBackgroundColor: string;
  buttonTextColor: string;
  id: BuilderBackgroundGradientOptionId;
  previewTextColor: string;
  previewValue: string;
  textColor: string;
  value: string;
};

const backgroundGradientPresets = [
  {
    from: "background",
    id: "soft",
    textColor: "var(--site-on-background)",
    to: "surface",
  },
  {
    from: "primary",
    id: "brand",
    textColor: "var(--site-on-primary)",
    to: "accent",
  },
  {
    from: "inverse",
    id: "inverse",
    textColor: "var(--site-on-inverse)",
    to: "primary",
  },
  {
    from: "surface",
    id: "surface",
    textColor: "var(--site-on-surface)",
    to: "background",
  },
] as const;

export function builderBackgroundThemeOptions(
  design: SiteBuilderDesign,
): BuilderBackgroundThemeOption[] {
  const resolved = mergeTemplateDesign(design);
  const variables = templateDesignToCssVars(resolved);
  const { colors } = resolved;

  return [
    {
      id: "default",
      accentColor: colors.accent,
      backgroundColor: colors.background,
      backgroundValue: "",
      buttonBackgroundColor: colors.primary,
      buttonTextColor: variables["--site-on-primary"],
      isDefault: true,
      textColor: variables["--site-on-background"],
      textValue: "",
    },
    {
      id: "background",
      accentColor: colors.accent,
      backgroundColor: colors.background,
      backgroundValue: "var(--site-background)",
      buttonBackgroundColor: colors.primary,
      buttonTextColor: variables["--site-on-primary"],
      textColor: variables["--site-on-background"],
      textValue: "var(--site-on-background)",
    },
    {
      id: "surface",
      accentColor: colors.accent,
      backgroundColor: colors.surface,
      backgroundValue: "var(--site-surface)",
      buttonBackgroundColor: colors.primary,
      buttonTextColor: variables["--site-on-primary"],
      textColor: variables["--site-on-surface"],
      textValue: "var(--site-on-surface)",
    },
    {
      id: "primary",
      accentColor: colors.accent,
      backgroundColor: colors.primary,
      backgroundValue: "var(--site-primary)",
      buttonBackgroundColor: colors.background,
      buttonTextColor: variables["--site-on-background"],
      textColor: variables["--site-on-primary"],
      textValue: "var(--site-on-primary)",
    },
    {
      id: "accent",
      accentColor: colors.primary,
      backgroundColor: colors.accent,
      backgroundValue: "var(--site-accent)",
      buttonBackgroundColor: colors.background,
      buttonTextColor: variables["--site-on-background"],
      textColor: variables["--site-on-accent"],
      textValue: "var(--site-on-accent)",
    },
    {
      id: "inverse",
      accentColor: colors.accent,
      backgroundColor: colors.text,
      backgroundValue: "var(--site-inverse)",
      buttonBackgroundColor: colors.background,
      buttonTextColor: variables["--site-on-background"],
      textColor: variables["--site-on-inverse"],
      textValue: "var(--site-on-inverse)",
    },
  ];
}

export function builderBackgroundGradientOptions(
  design: SiteBuilderDesign,
): BuilderBackgroundGradientOption[] {
  const resolved = mergeTemplateDesign(design);
  const variables = templateDesignToCssVars(resolved);
  const previewColors = {
    accent: resolved.colors.accent,
    background: resolved.colors.background,
    inverse: variables["--site-inverse"],
    primary: resolved.colors.primary,
    surface: resolved.colors.surface,
  };
  const previewTextColors = {
    "var(--site-on-background)": variables["--site-on-background"],
    "var(--site-on-inverse)": variables["--site-on-inverse"],
    "var(--site-on-primary)": variables["--site-on-primary"],
    "var(--site-on-surface)": variables["--site-on-surface"],
  };

  return backgroundGradientPresets.map((preset) => ({
    buttonBackgroundColor: resolved.colors.background,
    buttonTextColor: variables["--site-on-background"],
    id: preset.id,
    previewTextColor: previewTextColors[preset.textColor],
    previewValue: `linear-gradient(135deg, ${previewColors[preset.from]} 0%, ${previewColors[preset.to]} 100%)`,
    textColor: preset.textColor,
    value: `linear-gradient(135deg, var(--site-${preset.from}) 0%, var(--site-${preset.to}) 100%)`,
  }));
}
